"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { Icon } from "@/components/ui/Icon";
import { motion, AnimatePresence } from "framer-motion";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isBefore,
  startOfDay,
} from "date-fns";
import { DatePickerProps } from "./types";
import { tr } from "@/config/localeText";
import {
  RESERVATION_UI,
  WEEK_DAYS_BY_LOCALE,
  dateFnsLocale,
} from "./reservationFormUi";

export const DatePicker = ({
  value,
  onChange,
  label,
  id,
  onSelectNow,
  isNowSelected,
  locale = "fr",
}: DatePickerProps) => {
  const t = tr(RESERVATION_UI, locale).datePicker;
  const dfLocale = dateFnsLocale(locale);
  const WEEK_DAYS = tr(WEEK_DAYS_BY_LOCALE, locale);
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isFocused, setIsFocused] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const selectedDate = useMemo(() => (value ? new Date(value) : null), [value]);

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePrevMonth = () => {
    const newDate = subMonths(currentMonth, 1);
    if (isBefore(endOfMonth(newDate), startOfDay(new Date()))) {
      return;
    }
    setCurrentMonth(newDate);
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const handleSelectDate = (date: Date) => {
    if (isBefore(date, startOfDay(new Date()))) {
      return;
    }
    onChange(format(date, "yyyy-MM-dd"));
    setIsOpen(false);
    setIsFocused(false);
  };

  const handleNow = () => {
    const today = new Date();
    onChange(format(today, "yyyy-MM-dd"));
    onSelectNow?.();
    setIsOpen(false);
    setIsFocused(false);
  };

  const handleInputClick = () => {
    setIsOpen(true);
    setIsFocused(true);
  };

  return (
    <div className="relative w-full px-2 md:w-1/2" ref={pickerRef}>
      <label htmlFor={id} className="mb-2 block font-medium text-black">
        {label}
      </label>
      <div className="relative">
        <div
          className="absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 cursor-pointer text-gold-700"
          onClick={handleInputClick}
        >
          <Icon
            icon={isNowSelected ? "ph:calendar-check" : "ph:calendar"}
            className="h-5 w-5"
          />
        </div>
        <input
          type="text"
          id={id}
          value={
            isNowSelected && format(new Date(), "yyyy-MM-dd") === value
              ? t.today
              : value
                ? format(new Date(value), "dd/MM/yyyy", { locale: dfLocale })
                : ""
          }
          readOnly
          onClick={handleInputClick}
          className="h-12 w-full cursor-pointer rounded-lg border border-black/15 bg-white py-3 pe-4 ps-10 text-black outline-none transition-all placeholder:text-black/40 focus:border-gold-600 focus:ring-1 focus:ring-gold-300"
          placeholder={t.selectDate}
        />

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute z-[9999] mt-2 flex w-[320px] flex-col rounded-lg border border-black/15 bg-white shadow-[0_10px_28px_rgba(0,0,0,0.12)]"
            >
              <button
                onClick={handleNow}
                className="flex items-center gap-2 border-b border-black/15 p-3 text-sm text-black transition-colors hover:bg-black/5"
              >
                <Icon
                  icon="ph:calendar-check"
                  className="h-4 w-4 text-gold-700"
                />
                <span>{t.today}</span>
              </button>

              <div className="p-4">
                <div className="mb-4 flex items-center justify-between">
                  <button
                    title={t.prev}
                    onClick={handlePrevMonth}
                    className="rounded-full p-1 text-black transition-colors hover:bg-black/10"
                  >
                    <Icon icon="ph:caret-left" className="h-5 w-5" />
                  </button>
                  <span className="font-syne font-medium text-black">
                    {format(currentMonth, "MMMM yyyy", { locale: dfLocale })}
                  </span>
                  <button
                    title={t.next}
                    onClick={handleNextMonth}
                    className="rounded-full p-1 text-black transition-colors hover:bg-black/10"
                  >
                    <Icon icon="ph:caret-right" className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-2 grid grid-cols-7 gap-1 text-center">
                  {WEEK_DAYS.map((day) => (
                    <div
                      key={day}
                      className="text-xs font-medium text-black/60"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {Array.from({
                    // Décalage lundi-d'abord : getDay() renvoie 0 pour dimanche,
                    // donc (getDay()+6)%7 mappe lun→0 … dim→6 (en-tête lundi-d'abord).
                    length: (startOfMonth(currentMonth).getDay() + 6) % 7,
                  }).map((_, index) => (
                    <div key={`empty-${index}`} />
                  ))}
                  {days.map((day) => {
                    const isSelected =
                      selectedDate && isSameDay(day, selectedDate);
                    const isCurrentDay = isToday(day);
                    const isPastDay = isBefore(day, startOfDay(new Date()));

                    return (
                      <button
                        key={day.toString()}
                        onClick={() => handleSelectDate(day)}
                        disabled={isPastDay}
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${
                          isSelected
                            ? "bg-primary font-medium text-black"
                            : isPastDay
                              ? "cursor-not-allowed bg-black/5 text-black/30"
                              : isCurrentDay
                                ? "bg-black/10 font-medium text-black"
                                : "text-black hover:bg-black/10"
                        }`}
                      >
                        {format(day, "d")}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
